// frontend/src/pages/marketplace/VendorListPage.tsx
// Vendor directory page with filtering and search

import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search,
  MapPin,
  Shield,
  Star,
  Filter,
  Loader2,
  X,
  Grid,
  List,
  ChevronDown,
} from 'lucide-react';
import { VendorCard, MarketplaceSearch } from '../components';
import { useInfiniteVendors, useCategories } from '../hooks/useMarketplace';
import type { VendorFilters } from '../types/marketplaceTypes';

const VendorListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Build filters from URL
  const filters: VendorFilters = {
    search: searchParams.get('q') || undefined,
    categorySlug: searchParams.get('category') || undefined,
    state: searchParams.get('state') || undefined,
    isVerified: searchParams.get('verified') === 'true' ? true : undefined,
    minRating: searchParams.get('minRating') ? Number(searchParams.get('minRating')) : undefined,
    sortBy: (searchParams.get('sortBy') as VendorFilters['sortBy']) || 'rating',
    sortOrder: (searchParams.get('sortOrder') as VendorFilters['sortOrder']) || 'desc',
  };

  const {
    data: vendorsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteVendors(filters);

  const { data: categories } = useCategories();

  const vendors = vendorsData?.pages.flatMap((page) => page.items) || [];
  const totalCount = vendorsData?.pages[0]?.total || 0;

  const handleFilterChange = (key: string, value: string | boolean | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value !== undefined && value !== '' && value !== false) {
      params.set(key, String(value));
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const handleSearch = (query: string) => {
    handleFilterChange('q', query || undefined);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const activeFilterCount = [
    filters.search,
    filters.categorySlug,
    filters.state,
    filters.isVerified,
    filters.minRating,
  ].filter(Boolean).length;

  // US States for filter dropdown
  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <nav className="flex items-center gap-2 text-sm text-warm-gray mb-2">
                <Link to="/marketplace" className="hover:text-sage">
                  Marketplace
                </Link>
                <span>/</span>
                <span className="text-navy">Vendors</span>
              </nav>
              <h1 className="font-display text-2xl md:text-3xl font-semibold text-navy">
                Vendor Directory
              </h1>
              <p className="text-warm-gray mt-1">
                {totalCount} verified vendors ready to serve your family
              </p>
            </div>

            {/* Search */}
            <div className="w-full md:w-96">
              <MarketplaceSearch
                placeholder="Search vendors..."
                onSearch={handleSearch}
                showSuggestions={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border-b border-gray-100 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 md:pb-0">
            {/* Category Filter */}
            <div className="relative flex-shrink-0">
              <select
                value={filters.categorySlug || ''}
                onChange={(e) => handleFilterChange('category', e.target.value || undefined)}
                className="appearance-none pl-3 pr-8 py-2 bg-cream border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-sage/20 focus:border-sage"
              >
                <option value="">All Categories</option>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.slug}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray pointer-events-none" />
            </div>

            {/* State Filter */}
            <div className="relative flex-shrink-0">
              <select
                value={filters.state || ''}
                onChange={(e) => handleFilterChange('state', e.target.value || undefined)}
                className="appearance-none pl-3 pr-8 py-2 bg-cream border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-sage/20 focus:border-sage"
              >
                <option value="">All States</option>
                <option value="national">National Coverage</option>
                {US_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray pointer-events-none" />
            </div>

            {/* Verified Toggle */}
            <label className="flex items-center gap-2 px-3 py-2 bg-cream border border-gray-200 rounded-lg cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                checked={filters.isVerified || false}
                onChange={(e) => handleFilterChange('verified', e.target.checked || undefined)}
                className="w-4 h-4 text-sage border-gray-300 rounded focus:ring-sage"
              />
              <Shield className="w-4 h-4 text-sage" />
              <span className="text-sm text-navy whitespace-nowrap">Verified Only</span>
            </label>

            {/* Rating Filter */}
            <div className="relative flex-shrink-0">
              <select
                value={filters.minRating || ''}
                onChange={(e) =>
                  handleFilterChange('minRating', e.target.value ? Number(e.target.value) : undefined)
                }
                className="appearance-none pl-3 pr-8 py-2 bg-cream border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-sage/20 focus:border-sage"
              >
                <option value="">Any Rating</option>
                <option value="4">4+ Stars</option>
                <option value="4.5">4.5+ Stars</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray pointer-events-none" />
            </div>

            {/* Sort */}
            <div className="relative flex-shrink-0">
              <select
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('-');
                  handleFilterChange('sortBy', sortBy);
                  handleFilterChange('sortOrder', sortOrder);
                }}
                className="appearance-none pl-3 pr-8 py-2 bg-cream border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-sage/20 focus:border-sage"
              >
                <option value="rating-desc">Highest Rated</option>
                <option value="review_count-desc">Most Reviewed</option>
                <option value="name-asc">Name: A-Z</option>
                <option value="created_at-desc">Newest</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray pointer-events-none" />
            </div>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-sage hover:text-sage-dark flex-shrink-0"
              >
                <X className="w-4 h-4" />
                Clear ({activeFilterCount})
              </button>
            )}

            {/* View Toggle - Desktop */}
            <div className="hidden md:flex items-center border border-gray-200 rounded-lg overflow-hidden ml-auto flex-shrink-0">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-sage text-white'
                    : 'bg-white text-warm-gray hover:bg-cream'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-sage text-white'
                    : 'bg-white text-warm-gray hover:bg-cream'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-sage animate-spin" />
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-cream-dark rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-warm-gray" />
            </div>
            <h3 className="font-display text-lg font-semibold text-navy mb-2">
              No vendors found
            </h3>
            <p className="text-warm-gray mb-4">
              Try adjusting your filters or search criteria.
            </p>
            <button
              onClick={clearFilters}
              className="text-sage hover:text-sage-dark font-medium"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-warm-gray">
                Showing {vendors.length} of {totalCount} vendors
              </p>
            </div>

            {/* Vendor Grid/List */}
            <div
              className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                  : 'space-y-4'
              }
            >
              {vendors.map((vendor) => (
                <VendorCard
                  key={vendor.id}
                  vendor={vendor}
                  variant={viewMode === 'list' ? 'compact' : 'default'}
                />
              ))}
            </div>

            {/* Load More */}
            {hasNextPage && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
                >
                  {isFetchingNextPage ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading...
                    </span>
                  ) : (
                    'Load More Vendors'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VendorListPage;



